import { BaseProcedureStepWithOffsprings } from "../components/ProcedureRowContainer/ProcedureRowContainer";

export type ProcedureStepsByParent<T> = Record<StepId, T[] | undefined> & { root: T[] };
export function getProcedureStepsByParent<T extends { parentId?: NullableStepId }>(
  steps: T[] | undefined,
): ProcedureStepsByParent<T> {
  const init: ProcedureStepsByParent<T> = { root: [] };
  return (
    steps?.reduce((acc, step) => {
      const children = acc[step.parentId || "root"] || [];
      children.push(step);
      acc[step.parentId || "root"] = children;
      return acc;
    }, init) || init
  );
}

export type StepId = number | string;
export type NullableStepId = StepId | null;
export type ProcedureStepIdentifiers = { id: StepId; parentId?: NullableStepId };

// Using offsprings here to avoid confusion around react children props
export function getProcedureStepsWithOffsprings<T extends ProcedureStepIdentifiers>(
  steps: T[] | undefined,
): Array<BaseProcedureStepWithOffsprings<T>> {
  const stepsByParent = getProcedureStepsByParent(steps);
  const rootSteps = stepsByParent["root"];

  function getOffsprings(
    step: BaseProcedureStepWithOffsprings<T>,
  ): Array<BaseProcedureStepWithOffsprings<T>> {
    return (
      stepsByParent[step.id]?.map((offspring) => {
        const stepWithOffsprings: BaseProcedureStepWithOffsprings<T> = {
          ...offspring,
          parent: step,
          offsprings: [],
        };
        stepWithOffsprings.offsprings = getOffsprings(stepWithOffsprings);
        return stepWithOffsprings;
      }) || []
    );
  }

  return rootSteps.map((step) => {
    const stepWithOffsprings: BaseProcedureStepWithOffsprings<T> = {
      ...step,
      parent: null,
      offsprings: [],
    };
    stepWithOffsprings.offsprings = getOffsprings(stepWithOffsprings);
    return stepWithOffsprings;
  });
}
